// ============================================
// INFRASTRUCTURE - HTTP Client con Axios
// ============================================

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '../../domain/entities';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Función para obtener el token (se importa lazy para evitar circular dependency)
let getAccessToken: (() => string | null) | null = null;
let clearAuthState: (() => void) | null = null;
let setAccessToken: ((token: string, expiresInSeconds: number) => void) | null = null;

export const initAuthHelpers = (
  getTokenFn: () => string | null,
  clearStateFn: () => void,
  setTokenFn: (token: string, expiresInSeconds: number) => void
) => {
  getAccessToken = getTokenFn;
  clearAuthState = clearStateFn;
  setAccessToken = setTokenFn;
};

// ---------- Instancia base de Axios ----------
export const axiosClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // IMPORTANTE: envía cookies HttpOnly en cada request
});

// ---------- Request Interceptor ----------
axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // No enviar token para rutas públicas y rutas de autenticación
    // EXCEPCIÓN: Si la petición tiene sendTokenToPublic=true, enviar token aunque sea pública
    const isPublicRoute = config.url?.includes('/public/');
    const isAuthRoute = config.url?.includes('/auth/');
    const sendTokenToPublic = (config as any).sendTokenToPublic === true;
    
    if ((isPublicRoute || isAuthRoute) && !sendTokenToPublic) {
      return config;
    }
    
    // Obtener token del store
    const token = getAccessToken?.();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------- Response Interceptor ----------
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Cancelación deliberada (AbortSignal / queryClient.cancelQueries): se deja pasar
    // tal cual, sin convertirla en ApiError. Si se transformara, React Query dejaría de
    // reconocerla como cancelación y algún componente podría tratarla como un fallo real.
    if (axios.isCancel(error) || (error as AxiosError).code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    // Timeout del cliente (30s, ver el `timeout` de la instancia más arriba): mensaje
    // propio, distinto del de "sin conexión", para que el usuario sepa que sí hubo
    // respuesta del servidor esperándose y no un problema de red.
    if ((error as AxiosError).code === 'ECONNABORTED') {
      return Promise.reject({
        message: 'La consulta tardó demasiado. Intenta con un rango de fechas más corto.',
        statusCode: 0,
        timestamp: new Date().toISOString(),
      } as ApiError);
    }

    // Error de red u otro error sin response
    if (!error.response) {
      return Promise.reject({
        message: 'Error de conexión. Verifica tu internet.',
        statusCode: 0,
        timestamp: new Date().toISOString(),
      } as ApiError);
    }

    const { status } = error.response;

    // 401: Token expirado - intentar refresh (excepto para rutas de auth)
    const isAuthRoute = originalRequest.url?.includes('/auth/login') || 
                       originalRequest.url?.includes('/auth/refresh') ||
                       originalRequest.url?.includes('/auth/signup');
    
    if (status === 401 && !originalRequest._retry && !isAuthRoute) {
      if (isRefreshing) {
        // Si ya se está refrescando, esperar en cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Llamar a /auth/refresh (la cookie se envía automáticamente).
        // `axios.post` directo (no `axiosClient`) NO hereda el timeout de
        // 30s de la instancia base — sin uno propio acá, una respuesta que
        // nunca llega deja `isRefreshing` trabado en true para siempre, y
        // con él cualquier request 401 en cola (incluido logout) esperando
        // sin fin. Este timeout garantiza que el catch de abajo (que sí
        // limpia el estado y redirige a login) siempre termine corriendo.
        const { data } = await axios.post<{ token: string; expiresInSeconds: number }>(
          `${API_BASE_URL}/auth/refresh`,
          {}, // Body vacío
          { withCredentials: true, timeout: 10000 } // IMPORTANTE: envía la cookie HttpOnly
        );

        // Actualizar token en memoria
        if (setAccessToken) {
          setAccessToken(data.token, data.expiresInSeconds);
        }

        processQueue(null, data.token);
        isRefreshing = false;

        // Reintentar request original
        return axiosClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        isRefreshing = false;
        
        if (clearAuthState) {
          clearAuthState();
        }
        if ((window as any).electronAPI) {
          window.location.hash = '/auth/login';
        } else {
          window.location.href = '/auth/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    // Formatear error según respuesta del backend
    const apiError: ApiError = {
      message: error.response.data?.message || 'Error desconocido',
      statusCode: status,
      code: error.response.data?.code,
      fieldErrors: error.response.data?.fieldErrors,
      timestamp: error.response.data?.timestamp || new Date().toISOString(),
    };

    return Promise.reject(apiError);
  }
);

// ---------- Helper para manejar errores ----------
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error) && error.response?.data) {
    return error.response.data as ApiError;
  }
  if ((error as ApiError).statusCode !== undefined) {
    return error as ApiError;
  }
  return {
    message: 'Error inesperado',
    statusCode: 500,
    timestamp: new Date().toISOString(),
  };
};
